import React, {useEffect, useRef, useState} from "react";

export type SplitViewProps = {
	/** Fraction (0-1) of the container's main-axis length taken by the first panel. */
	firstPercent: number;
	/** Called while the user drags the divider, with the new fraction for the first panel. */
	onFirstPercentChange: (percent: number)=>void;
	/** "column" = first panel on top, divider horizontal. "row" = first panel on left, divider vertical. */
	direction?: "column" | "row";
	/** Clamp-range for firstPercent, as fractions (0-1). */
	minFirstPercent?: number;
	maxFirstPercent?: number;
	/** Thickness of the divider strip, in px. */
	dividerSize?: number;
	dividerStyle?: any;
	children: [React.ReactNode, React.ReactNode];
};

/** Generic draggable split-view. Splits the available space between 2 panels, with a draggable divider in between.
 * Note: this is a controlled component -- the caller is expected to hold the split-position (eg. in a persisted mobx property),
 * and pass it back in via `firstPercent`. */
export const SplitView = (props: SplitViewProps)=>{
	const {firstPercent, onFirstPercentChange, direction = "column", minFirstPercent = .15, maxFirstPercent = .85, dividerSize = 5, dividerStyle, children} = props;
	const containerRef = useRef<HTMLDivElement>(null);
	const [containerSize, setContainerSize] = useState(0);
	const [dragging, setDragging] = useState(false);
	const draggingRef = useRef(false);

	useEffect(()=>{
		const el = containerRef.current;
		if (el == null) return;
		const update = ()=>setContainerSize(direction == "column" ? el.offsetHeight : el.offsetWidth);
		update();
		const ro = new ResizeObserver(update);
		ro.observe(el);
		return ()=>ro.disconnect();
	}, [direction]);

	const clampPercent = (percent: number)=>Math.min(maxFirstPercent, Math.max(minFirstPercent, percent));
	// guard against corrupted persisted values (eg. NaN or out-of-range, from older versions of the state-shape)
	const percent = Number.isFinite(firstPercent) ? clampPercent(firstPercent) : clampPercent(.5);
	const sizeKnown = containerSize > 0;
	const firstSize = containerSize * percent;
	const secondSize = containerSize - firstSize - dividerSize;

	const paneStyle = (size: number, grow: number): React.CSSProperties=>{
		return {
			display: "flex",
			minHeight: 0, minWidth: 0,
			overflow: "hidden",
			// if container size is not yet known, fall back to proportional flex-grow (eg. the first frame, before the ResizeObserver fires)
			...(sizeKnown
				? {flex: "0 0 auto", ...(direction == "column" ? {height: size} : {width: size})}
				: {flex: `${grow} 1 0%`}),
		};
	};

	const onPointerDown = (e: React.PointerEvent<HTMLDivElement>)=>{
		e.preventDefault();
		e.stopPropagation();
		e.currentTarget.setPointerCapture(e.pointerId);
		draggingRef.current = true;
		setDragging(true);
	};
	const onPointerMove = (e: React.PointerEvent<HTMLDivElement>)=>{
		if (!draggingRef.current) return;
		const el = containerRef.current;
		if (el == null) return;
		const rect = el.getBoundingClientRect();
		const size = direction == "column" ? rect.height : rect.width;
		if (size <= 0) return;
		const pos = direction == "column" ? e.clientY - rect.top : e.clientX - rect.left;
		onFirstPercentChange(clampPercent(pos / size));
	};
	const onPointerEnd = (e: React.PointerEvent<HTMLDivElement>)=>{
		if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
		draggingRef.current = false;
		setDragging(false);
	};

	return (
		<div ref={containerRef} style={{
			display: "flex", flexDirection: direction,
			flex: 1, minHeight: 0, minWidth: 0,
			overflow: "hidden",
		}}>
			<div style={paneStyle(firstSize, percent)}>{children[0]}</div>
			<div style={{
				flex: "0 0 auto",
				...(direction == "column" ? {height: dividerSize, cursor: "row-resize"} : {width: dividerSize, cursor: "col-resize"}),
				background: dragging ? "rgba(0,0,0,.6)" : "rgba(0,0,0,.3)",
				touchAction: "none",
				userSelect: "none",
				...dividerStyle,
			}}
				onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerEnd} onPointerCancel={onPointerEnd} onLostPointerCapture={onPointerEnd}
			/>
			<div style={paneStyle(secondSize, 1 - percent)}>{children[1]}</div>
		</div>
	);
};